import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Friend } from '../../types';

const FriendProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { friendId } = useParams<{ friendId: string }>();
  const navigate = useNavigate();
  const [friend, setFriend] = useState<Friend | null>(null);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (friendId) {
      fetchFriendDetails();
    }
  }, [friendId]);

  const fetchFriendDetails = async () => {
    try {
      setLoading(true);
      // Get friend from friends list
      const response = await api.get('/friends');
      const foundFriend = response.data.friends?.find((f: Friend) => f.id === friendId);
      if (foundFriend) {
        setFriend(foundFriend);
      } else {
        toast.error(t('friends.friendNotFound'));
        navigate('/friends');
      }
    } catch (error: any) {
      console.error('Error fetching friend details:', error);
      toast.error(t('friends.friendLoadError'));
      navigate('/friends');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFriend = async () => {
    if (!friend) return;
    
    if (!window.confirm(t('friends.removeFriendConfirm', { name: `${friend.firstName} ${friend.lastName}` }))) {
      return;
    }

    setRemoving(true);
    try {
      await api.delete(`/friends/${friend.id}`);
      toast.success(t('friends.friendRemoved'));
      navigate('/friends');
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('friends.removeFriendError'));
    } finally {
      setRemoving(false);
    }
  };

  const getInitials = () => {
    if (friend?.firstName && friend?.lastName) {
      return `${friend.firstName[0]}${friend.lastName[0]}`.toUpperCase();
    }
    return friend?.email?.[0].toUpperCase() || 'U';
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-lg">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (!friend) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/friends')}
          className="mb-4 text-primary-600 hover:text-primary-700"
        >
          ← {t('common.back')}
        </button>
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-500">{t('friends.friendNotFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/friends')}
        className="mb-4 text-primary-600 hover:text-primary-700"
      >
        ← {t('common.back')}
      </button>

      {/* Profile Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center space-x-6">
          {friend.avatarUrl ? (
            <img
              src={friend.avatarUrl}
              alt={`${friend.firstName} ${friend.lastName}`}
              className="w-24 h-24 rounded-full object-cover"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-primary-600 flex items-center justify-center text-white text-2xl font-bold">
              {getInitials()}
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">
              {friend.firstName} {friend.lastName}
            </h1>
            <p className="text-gray-600 mb-2">{friend.email}</p>
            {friend.loyaltyNumber && (
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-800 text-sm font-medium rounded-full">
                {t('profile.loyaltyNumber')}: {friend.loyaltyNumber}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('friends.actions')}</h2>
        <div className="space-y-3">
          <Link
            to={`/messages?friendId=${friend.id}`}
            className="block w-full px-4 py-2 bg-primary-600 text-white text-center rounded-md hover:bg-primary-700"
          >
            {t('friends.sendMessage')}
          </Link>
          <button
            onClick={handleRemoveFriend}
            disabled={removing}
            className="w-full px-4 py-2 border border-red-600 text-red-600 rounded-md hover:bg-red-50 disabled:opacity-50"
          >
            {removing ? t('common.loading') : t('friends.remove')}
          </button>
        </div>
      </div>

      {/* Friend Since */}
      {friend.friendsSince && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-gray-700">
              {t('friends.friendsSince')} {format(new Date(friend.friendsSince), 'MMM d, yyyy')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FriendProfilePage;
